import React, { useState, useMemo } from 'react';
import readXlsxFile from 'read-excel-file';
import api from '../api';
import { Segmente, Icone, libelleType, messageErreur } from './commun';

// Import d'une liste de comptes depuis Excel (.xlsx) ou CSV : liste clients
// extraite d'ISAVIGNES, fichiers de prospects, carnet d'adresses de salon.
// Le fichier est lu dans le navigateur ; le serveur valide chaque ligne comme
// une saisie et ignore les doublons sans jamais rien écraser.

const CHAMPS = [
  { cle: 'nom', libelle: 'Nom de l’établissement', requis: true },
  { cle: 'type', libelle: 'Type de client' },
  { cle: 'ville', libelle: 'Ville' },
  { cle: 'code_postal', libelle: 'Code postal' },
  { cle: 'adresse', libelle: 'Adresse' },
  { cle: 'telephone', libelle: 'Téléphone' },
  { cle: 'email', libelle: 'E-mail' },
  { cle: 'contact_nom', libelle: 'Contact' },
  { cle: 'code_erp', libelle: 'Code client ERP' },
  { cle: 'notes', libelle: 'Notes' },
];
// Ordre de reconnaissance : le plus spécifique d'abord (« code postal » avant
// « code », « nom du contact » avant « nom »).
const RECONNAISSANCE = [
  ['code_postal', /code.?postal|^cp$/],
  ['email', /mail/],
  ['telephone', /tel|phone|portable|mobile|fixe/],
  ['contact_nom', /contact|interlocuteur|responsable|gerant/],
  ['code_erp', /^code|code.?client|n.?client|^numero|^ref|^id$/],
  ['ville', /ville|commune|localite/],
  ['adresse', /adresse|rue|voie/],
  ['type', /^type|cat.?gorie|famille|activite|segment|canal/],
  ['notes', /note|commentaire|observation|remarque/],
  ['nom', /raison|nom|client|enseigne|societe|denomination|etablissement/],
];
const normaliser = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').trim();

// Le type écrit dans le fichier, ramené aux cinq types de l'outil.
export function typeDepuis(t) {
  const s = normaliser(t);
  if (!s) return 'autre';
  if (/cav/.test(s)) return 'caviste';
  if (/resto|restau|bistro|brasserie|hotel|traiteur|bar\b|chr/.test(s)) return 'restaurateur';
  if (/bio|epicerie fine|vrac/.test(s)) return 'bio';
  if (/distrib|grossiste|negoc|central|importateur/.test(s)) return 'distributeur';
  return 'autre';
}

function lireCsv(texte) {
  const premiere = texte.split(/\r?\n/)[0] || '';
  const sep = [';', '\t', ','].sort((a, b) => premiere.split(b).length - premiere.split(a).length)[0];
  const lignes = []; let ligne = []; let champ = ''; let guillemets = false;
  for (let i = 0; i < texte.length; i++) {
    const c = texte[i];
    if (guillemets) {
      if (c === '"' && texte[i + 1] === '"') { champ += '"'; i++; } else if (c === '"') guillemets = false; else champ += c;
    } else if (c === '"') guillemets = true;
    else if (c === sep) { ligne.push(champ); champ = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && texte[i + 1] === '\n') i++;
      ligne.push(champ); lignes.push(ligne); ligne = []; champ = '';
    } else champ += c;
  }
  if (champ || ligne.length) { ligne.push(champ); lignes.push(ligne); }
  return lignes;
}

async function lireFichier(f) {
  if (/\.xlsx$/i.test(f.name)) return readXlsxFile(f);
  if (/\.csv$|\.txt$/i.test(f.name)) {
    const buf = await f.arrayBuffer();
    let t = new TextDecoder('utf-8').decode(buf);
    // Un CSV enregistré par Excel sous Windows n'est pas en UTF-8 : les accents
    // sortent en « � ». On relit alors en Windows-1252.
    if (t.includes('�')) t = new TextDecoder('windows-1252').decode(buf);
    return lireCsv(t.replace(/^﻿/, ''));
  }
  throw new Error('Format non reconnu : choisissez un fichier .xlsx ou .csv. Un ancien .xls s’enregistre en .xlsx depuis Excel.');
}

const cellule = (v) => (v == null ? '' : v instanceof Date ? v.toLocaleDateString('fr-FR') : String(v).trim());

export default function Import({ utilisateur, referentiel, onFerme, onFini }) {
  const [fichier, setFichier] = useState(null);
  const [entetes, setEntetes] = useState([]);
  const [donnees, setDonnees] = useState([]);
  const [correspondance, setCorrespondance] = useState({});
  const [statut, setStatut] = useState('prospect');
  const [societe, setSociete] = useState(utilisateur.societe_id ? String(utilisateur.societe_id) : '');
  const [commercial, setCommercial] = useState('');
  const [erreur, setErreur] = useState('');
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);

  const choisir = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setErreur(''); setResultat(null);
    try {
      const lignes = (await lireFichier(f)).map((l) => l.map(cellule));
      const iEntete = lignes.findIndex((l) => l.filter(Boolean).length >= 2);
      if (iEntete < 0) throw new Error('Le fichier ne contient pas de ligne de titres reconnaissable.');
      const ent = lignes[iEntete].map((h, i) => h || `Colonne ${i + 1}`);
      const corps = lignes.slice(iEntete + 1).filter((l) => l.some(Boolean));
      const auto = {};
      ent.forEach((h, i) => {
        const n = normaliser(h);
        const trouve = RECONNAISSANCE.find(([cle, re]) => re.test(n) && auto[cle] === undefined);
        if (trouve) auto[trouve[0]] = i;
      });
      setFichier(f); setEntetes(ent); setDonnees(corps); setCorrespondance(auto);
    } catch (err) { setErreur(err.message || 'Lecture du fichier impossible.'); setFichier(null); }
  };

  const lignes = useMemo(() => donnees.map((l) => {
    const o = {};
    CHAMPS.forEach(({ cle }) => { const i = correspondance[cle]; if (i !== undefined && i !== '') o[cle] = l[i] || ''; });
    o.type = typeDepuis(o.type);
    return o;
  }), [donnees, correspondance]);
  const valides = lignes.filter((l) => l.nom);

  const importer = async () => {
    setEnvoi(true); setErreur('');
    try {
      const { data } = await api.post('/commercial/import', {
        lignes, statut, societe_id: societe || undefined, commercial_id: commercial || undefined,
        origine: `Import ${fichier.name}`.slice(0, 150),
      });
      setResultat(data); onFini();
    } catch (e) { setErreur(messageErreur(e, 'L’import a échoué.')); }
    finally { setEnvoi(false); }
  };

  const utilisateursSociete = (referentiel?.utilisateurs || []).filter((u) => !societe || !u.societe_id || String(u.societe_id) === societe);

  return (
    <div className="voile" role="dialog" aria-modal="true" aria-labelledby="titre-import">
      <div className="modale modale-large">
        <div className="modale-entete">
          <h2 id="titre-import">Importer des comptes</h2>
          <button type="button" className="bouton-icone" onClick={onFerme} aria-label="Fermer"><Icone.Fermer /></button>
        </div>

        {resultat ? (
          <div className="import-resultat">
            <p className="gros"><strong>{resultat.crees}</strong> compte{resultat.crees > 1 ? 's' : ''} créé{resultat.crees > 1 ? 's' : ''}.</p>
            {resultat.ignores.length > 0 && (
              <>
                <p>{resultat.ignores.length} ligne{resultat.ignores.length > 1 ? 's' : ''} ignorée{resultat.ignores.length > 1 ? 's' : ''}, sans rien modifier :</p>
                <ul className="ignores">
                  {resultat.ignores.slice(0, 50).map((x, i) => <li key={i}>Ligne {x.ligne}{x.nom ? ` · ${x.nom}` : ''} : {x.raison}</li>)}
                  {resultat.ignores.length > 50 && <li>… et {resultat.ignores.length - 50} autres.</li>}
                </ul>
              </>
            )}
            <div className="actions"><button type="button" onClick={onFerme}>Terminer</button></div>
          </div>
        ) : (
          <>
            <p className="aide">
              Fichier Excel (.xlsx) ou CSV, avec une ligne de titres. Les colonnes sont reconnues d'après leurs titres ;
              corrigez-les si besoin. Un compte déjà présent (même code client, ou même nom dans la même ville) est ignoré, jamais écrasé.
            </p>
            <label className="choix-fichier">
              <Icone.Import /> {fichier ? fichier.name : 'Choisir un fichier'}
              <input type="file" accept=".xlsx,.csv,.txt" onChange={choisir} />
            </label>

            {fichier && (
              <>
                <h3>Colonnes</h3>
                <div className="correspondance">
                  {CHAMPS.map((c) => (
                    <label key={c.cle}>
                      <span>{c.libelle}{c.requis && ' *'}</span>
                      <select value={correspondance[c.cle] ?? ''} onChange={(e) => setCorrespondance({ ...correspondance, [c.cle]: e.target.value === '' ? undefined : Number(e.target.value) })}>
                        <option value="">(aucune)</option>
                        {entetes.map((h, i) => <option key={i} value={i}>{h}</option>)}
                      </select>
                    </label>
                  ))}
                </div>
                <p className="aide">
                  Le type est déduit du texte de la colonne : « cave » donne Caviste, « restaurant », « bistrot » ou « hôtel »
                  donnent Restaurateur, « bio » donne Magasin bio, « distributeur » ou « grossiste » donnent Distributeur. Le reste devient « Autre ».
                </p>

                <h3>Ces comptes sont des…</h3>
                <Segmente etiquette="Statut des comptes importés" valeur={statut} onChange={setStatut}
                  options={[{ cle: 'prospect', libelle: 'Prospects' }, { cle: 'client', libelle: 'Clients existants' }]} />
                <p className="aide">
                  Des clients importés ne comptent pas comme « signés » dans le tableau de bord : seuls les passages au statut
                  Client faits dans l'outil sont comptés, sinon l'import gonflerait les résultats du jour.
                </p>

                <div className="deux-colonnes">
                  {!utilisateur.societe_id && (
                    <label><span>Société *</span>
                      <select value={societe} onChange={(e) => { setSociete(e.target.value); setCommercial(''); }}>
                        <option value="">Choisir…</option>
                        {referentiel?.societes.map((s) => <option key={s.id} value={s.id}>{s.nom}</option>)}
                      </select>
                    </label>
                  )}
                  <label><span>Commercial attribué</span>
                    <select value={commercial} onChange={(e) => setCommercial(e.target.value)}>
                      <option value="">Personne pour l'instant</option>
                      {utilisateursSociete.map((u) => <option key={u.id} value={u.id}>{u.nom || u.identifiant}</option>)}
                    </select>
                  </label>
                </div>

                <h3>Aperçu · {valides.length} compte{valides.length > 1 ? 's' : ''} à importer{lignes.length > valides.length ? `, ${lignes.length - valides.length} ligne(s) sans nom ignorée(s)` : ''}</h3>
                <div className="tableau">
                  <table>
                    <thead><tr><th>Nom</th><th>Type</th><th>Ville</th><th>Téléphone</th><th>Code ERP</th></tr></thead>
                    <tbody>
                      {valides.slice(0, 5).map((l, i) => (
                        <tr key={i}><td>{l.nom}</td><td>{libelleType(l.type)}</td><td>{l.ville}</td><td>{l.telephone}</td><td>{l.code_erp}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
            {erreur && <div className="erreur">{erreur}</div>}
            <div className="actions">
              <button type="button" className="lien" onClick={onFerme}>Annuler</button>
              <button type="button" onClick={importer}
                disabled={!fichier || envoi || correspondance.nom === undefined || !valides.length || (!utilisateur.societe_id && !societe)}>
                {envoi ? 'Import…' : `Importer ${valides.length} compte${valides.length > 1 ? 's' : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
